import {
  Calendar,
  Compass,
  Heart,
  Sparkles,
  Building2,
  Search,
  UserSquare,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@nx/ui';
import { useI18n } from '@nx/i18n';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logoutRequested } from '../store/slices/authSlice';

export function Dashboard() {
  const dispatch = useAppDispatch();
  const { t } = useI18n();
  const { identity } = useAppSelector((s) => s.auth);

  const handleSignOut = () => {
    dispatch(logoutRequested());
  };

  const stats = [
    {
      label: t('dashboard.stats.activeProposals'),
      value: '0',
      icon: Sparkles,
    },
    {
      label: t('dashboard.stats.confirmedBookings'),
      value: '0',
      icon: Calendar,
    },
    {
      label: t('dashboard.stats.upcomingRetreats'),
      value: '0',
      icon: Compass,
    },
    {
      label: t('dashboard.stats.savedPackages'),
      value: '0',
      icon: Heart,
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="border-b border-[#3D3328]/10 bg-white">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-4 md:px-8">
          <div>
            <Link
              to="/"
              className="text-xl md:text-2xl font-extrabold tracking-tight text-[#1A1A1A]"
              style={{ fontFamily: 'var(--font-sans)' }}
            >
              N‑Excelsior
            </Link>
          </div>
          <Button variant="outline" size="sm" onClick={handleSignOut}>
            {t('common.signOut')}
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-8">
        <section>
          <p className="text-xs uppercase tracking-[0.2em] text-[#8A7A6D]">
            {t('dashboard.welcome')}
          </p>
          <h1
            className="mt-1 text-2xl md:text-3xl font-bold text-[#1A1A1A]"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {identity?.displayName ?? identity?.email ?? t('dashboard.title')}
          </h1>
          {identity?.companyName ? (
            <p className="mt-2 inline-flex items-center gap-2 text-sm text-[#78665A]">
              <Building2 className="h-4 w-4" />
              {identity.companyName}
            </p>
          ) : null}
        </section>

        <section className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {stats.map((stat) => (
            <article
              key={stat.label}
              className="rounded-2xl border border-[#3D3328]/10 bg-white p-5"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-[#8A7A6D]">
                  {stat.label}
                </p>
                <stat.icon className="h-4 w-4 text-[#8A7A6D]" />
              </div>
              <p className="mt-3 text-2xl font-semibold text-[#1A1A1A]">{stat.value}</p>
            </article>
          ))}
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-[#1A1A1A]">
            {t('dashboard.quickActionsTitle')}
          </h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {identity?.partnerRole === 'eventOrganizer' ? (
              <QuickAction
                icon={Calendar}
                title={t('dashboard.eo.open')}
                description={t('dashboard.eo.desc')}
                href="/eo/events"
              />
            ) : null}
            <QuickAction
              icon={UserSquare}
              title={t('profile.dashboard.action')}
              description={t('profile.dashboard.desc')}
              href="/profile"
            />
            <QuickAction
              icon={Search}
              title={t('profile.discover.dashboard.action')}
              description={t('profile.discover.dashboard.desc')}
              href="/discover"
            />
            <QuickAction
              icon={Building2}
              title={t('dashboard.action.companyProfile')}
              description="Manage your company information and team contacts."
              href="/dashboard"
            />
          </div>
        </section>
      </main>
    </div>
  );
}

interface QuickActionProps {
  icon: typeof Sparkles;
  title: string;
  description: string;
  href: string;
}

function QuickAction({ icon: Icon, title, description, href }: QuickActionProps) {
  return (
    <Link
      to={href}
      className="group flex flex-col rounded-2xl border border-[#3D3328]/10 bg-white p-5 transition hover:border-[#1A1A1A]/30 hover:shadow-md"
    >
      <Icon className="h-5 w-5 text-[#1A1A1A]" />
      <h3 className="mt-3 text-base font-semibold text-[#1A1A1A]">{title}</h3>
      <p className="mt-2 text-sm text-[#78665A]">{description}</p>
    </Link>
  );
}
